import { useMutation } from '@tanstack/react-query';
import {
  createSurveyResponse,
  submitSurveyAnswer,
  completeSurveyResponse,
  ApiSurveyQuestion,
} from '../../services/surveyBackendService';
import { ApiError } from '../../lib/api';

interface SubmitSurveyParams {
  surveyId: string;
  questions: ApiSurveyQuestion[];
  answers: Record<number, string | string[]>;
}

/**
 * Hook for submitting a complete survey response using TanStack Query
 * Handles creating the response, submitting all answers in parallel, and completing the response
 * @returns Mutation object for survey submission
 */
export const useSubmitSurvey = () => {
  return useMutation<void, ApiError, SubmitSurveyParams>({
    mutationKey: ['submitSurvey'],
    mutationFn: async ({ surveyId, questions, answers }) => {
      // Step 1: Create the response
      const response = await createSurveyResponse({
        surveyId,
        respondentType: 'user',
      });

      // Step 2: Submit all answers in parallel for better performance
      const answerPromises = questions
        .map((question, index) => {
          const answer = answers[index];
          if (!answer || !question) return null;

          const answerArray = Array.isArray(answer) ? answer : [answer];
          return submitSurveyAnswer({
            value: answerArray,
            responseId: response.id,
            questionId: question.id,
            analysisKeyId: question.analysisKeyId,
          });
        })
        .filter(Boolean);

      await Promise.all(answerPromises);

      // Step 3: Mark response as complete
      await completeSurveyResponse(response.id);
    },
  });
};
